import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  Search,
  Trash2,
  Warehouse,
  Store,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Permission lists
const ORDER_USERS = ['arbe', 'admin', 'pabs', 'nicole', 'alma', 'jelyn', 'kristine', 'carla', 'cristel', 'geraldine'];
const APPROVE_USERS = ['arbe', 'admin', 'pabs', 'nicole', 'alma', 'jelyn', 'kristine', 'carla'];

const BodegaToNASOrders = ({ embedded = false }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Forms
  const [orderForm, setOrderForm] = useState({
    orderDate: new Date().toISOString().split('T')[0],
    items: [],
    notes: ''
  });
  
  const [newItem, setNewItem] = useState({
    productId: '',
    productName: '',
    quantity: '',
    unit: 'sack',
    bodegaStock: 0
  });
  
  const [rejectReason, setRejectReason] = useState('');

  // Access check
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin' || username === 'arbe';
  
  const canCreateOrders = isAdmin || ORDER_USERS.some(n => username.includes(n) || fullName.includes(n));
  const canApproveOrders = isAdmin || APPROVE_USERS.some(n => username.includes(n) || fullName.includes(n));

  useEffect(() => {
    loadOrders();
    loadProducts();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/nas/bodega-to-nas/orders`);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API}/api/nas/products`);
      // Load all products that have bodega stock > 0 (available for transfer)
      setProducts((res.data || []).filter(p => (p.bodegaStock || 0) > 0));
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Add item to order
  const addItemToOrder = () => {
    if (!newItem.productName || !newItem.quantity) {
      toast.error('Please fill in product and quantity');
      return;
    }
    
    const qty = parseFloat(newItem.quantity);
    if (qty > newItem.bodegaStock) {
      toast.error(`Only ${newItem.bodegaStock} available in Bodega`);
      return;
    }
    
    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, {
        ...newItem,
        quantity: qty
      }]
    });
    
    setNewItem({
      productId: '',
      productName: '',
      quantity: '',
      unit: 'sack',
      bodegaStock: 0
    });
  };

  // Remove item from order
  const removeItemFromOrder = (index) => {
    setOrderForm({
      ...orderForm,
      items: orderForm.items.filter((_, i) => i !== index)
    });
  };

  // Create order
  const handleCreateOrder = async () => {
    if (orderForm.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      const payload = {
        ...orderForm,
        createdBy: user?.full_name || user?.username
      };
      
      await axios.post(`${API}/api/nas/bodega-to-nas/orders`, payload);
      toast.success('Transfer order created successfully!');
      setShowCreateDialog(false);
      resetOrderForm();
      loadOrders();
      loadProducts(); // Refresh available stock
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  // Approve order - Transfer inventory
  const handleApproveOrder = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.put(`${API}/api/nas/bodega-to-nas/orders/${selectedOrder.id}/approve`, {
        approvedBy: user?.full_name || user?.username
      });
      toast.success('Order approved! Inventory transferred from Bodega to NAS Shop.');
      setShowApproveDialog(false);
      setSelectedOrder(null);
      loadOrders();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve order');
    }
  };

  // Reject order
  const handleRejectOrder = async () => {
    if (!selectedOrder || !rejectReason) {
      toast.error('Please provide a reason');
      return;
    }
    
    try {
      await axios.put(`${API}/api/nas/bodega-to-nas/orders/${selectedOrder.id}/reject`, {
        rejectedBy: user?.full_name || user?.username,
        reason: rejectReason
      });
      toast.success('Order rejected');
      setShowRejectDialog(false);
      setSelectedOrder(null);
      setRejectReason('');
      loadOrders();
    } catch (error) {
      toast.error('Failed to reject order');
    }
  };

  const resetOrderForm = () => {
    setOrderForm({
      orderDate: new Date().toISOString().split('T')[0],
      items: [],
      notes: ''
    });
    setNewItem({
      productId: '',
      productName: '',
      quantity: '',
      unit: 'sack',
      bodegaStock: 0
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const { color, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchTerm || 
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.items?.some(item => item.productName?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesTab = activeTab === 'all' || order.status === activeTab;
    
    return matchesSearch && matchesTab;
  });

  const orderStats = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    approved: orders.filter(o => o.status === 'approved').length
  };

  return (
    <div className={embedded ? '' : 'p-4 lg:p-6'}>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Warehouse className="text-blue-600" />
            <ArrowRight size={20} />
            <Store className="text-purple-600" />
            Bodega to NAS Shop Transfer
          </h1>
          <p className="text-slate-500">Transfer inventory from Bodega to NAS Shop</p>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        {canCreateOrders && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={18} className="mr-2" />
            New Transfer Request
          </Button>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Warehouse className="text-blue-600" size={24} />
            <ArrowRight className="text-slate-400" size={18} />
            <Store className="text-purple-600" size={24} />
          </div>
          <div>
            <p className="font-semibold text-blue-800">Bodega → NAS Shop Transfer</p>
            <p className="text-sm text-blue-600">When approved, inventory is deducted from Bodega and added to NAS Shop.</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="all">All ({orderStats.all})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({orderStats.pending})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({orderStats.approved})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No transfer orders found</p>
            {canCreateOrders && (
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4 bg-blue-600 hover:bg-blue-700">
                <Plus size={18} className="mr-2" />
                Create First Transfer
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <Card key={order.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">{order.orderNumber || `BTN-${order.id?.slice(0, 8)}`}</h3>
                      {getStatusBadge(order.status)}
                    </div>
                    <div className="text-sm text-slate-500 space-y-1">
                      <p>Created: {formatDate(order.createdAt)} by {order.createdBy}</p>
                      {order.approvedAt && <p>Approved: {formatDate(order.approvedAt)} by {order.approvedBy}</p>}
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-slate-400 mb-1">Items to Transfer:</p>
                      <div className="flex flex-wrap gap-1">
                        {order.items?.map((item, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {item.productName} x{item.quantity} {item.unit}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-lg font-semibold text-slate-600">{order.items?.length || 0} items</p>
                    <div className="flex gap-2 mt-2 justify-end">
                      {order.status === 'pending' && canApproveOrders && (
                        <>
                          <Button 
                            size="sm" 
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowApproveDialog(true);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle size={14} className="mr-1" />
                            Approve & Transfer
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowRejectDialog(true);
                            }}
                          >
                            <XCircle size={14} className="mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Order Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="text-blue-600" />
              <ArrowRight size={18} />
              <Store className="text-purple-600" />
              Transfer from Bodega to NAS Shop
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <strong>Note:</strong> Items will be deducted from Bodega and added to NAS Shop inventory when approved.
            </div>
            
            <div>
              <Label>Transfer Date</Label>
              <Input
                type="date"
                value={orderForm.orderDate}
                onChange={(e) => setOrderForm({...orderForm, orderDate: e.target.value})}
              />
            </div>

            {/* Add Item Section */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Add Item from Bodega</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Select 
                    value={newItem.productId}
                    onValueChange={(v) => {
                      const product = products.find(p => p.id === v);
                      setNewItem({
                        ...newItem,
                        productId: v,
                        productName: product?.name || '',
                        bodegaStock: product?.bodegaStock || 0,
                        unit: product?.unitType?.toLowerCase().includes('sack') ? 'sack' : 'pcs'
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select product from Bodega" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} (Bodega: {p.bodegaStock || 0})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({...newItem, quantity: e.target.value})}
                    max={newItem.bodegaStock}
                  />
                  <Select value={newItem.unit} onValueChange={(v) => setNewItem({...newItem, unit: v})}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sack">Sack</SelectItem>
                      <SelectItem value="kg">KG</SelectItem>
                      <SelectItem value="pcs">Pcs</SelectItem>
                      <SelectItem value="box">Box</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {newItem.productId && (
                <p className="text-xs text-slate-500 mt-1">Available in Bodega: {newItem.bodegaStock}</p>
              )}
              <Button onClick={addItemToOrder} className="mt-2 bg-blue-600 hover:bg-blue-700" size="sm">
                <Plus size={14} className="mr-1" /> Add to Transfer
              </Button>
            </div>

            {/* Order Items */}
            {orderForm.items.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Items to Transfer ({orderForm.items.length})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {orderForm.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{item.productName}</span>
                        <span className="text-slate-500 ml-2">{item.quantity} {item.unit}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeItemFromOrder(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                placeholder="Reason for transfer..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateOrder} className="bg-blue-600 hover:bg-blue-700">
              Create Transfer Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Transfer Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="text-green-600" />
              Approve Transfer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-3 rounded-lg text-sm text-green-700">
              <strong>Confirm:</strong> This will deduct items from Bodega and add them to NAS Shop inventory.
            </div>
            
            {selectedOrder && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Items to Transfer</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <span>{item.productName}</span>
                      <span className="font-medium">{item.quantity} {item.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button onClick={handleApproveOrder} className="bg-green-600 hover:bg-green-700">
              <CheckCircle size={16} className="mr-2" />
              Approve & Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Transfer Request</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Reason for Rejection</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button onClick={handleRejectOrder} variant="destructive">
              Reject Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BodegaToNASOrders;
